import { useEffect } from 'react';
import { useAuthtoken, logout } from 'stores/useAuthStore';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';

import request from 'utils/request';
import { setAppLoading, showSnackbar } from 'stores/useUiStore';
import type { ApiRespBody } from 'types/api';
import type { Connector, ConnectorType } from './connectors.types';


const TOKEN_INVALID = 'Token is not valid';

export const CONNECTORS_QUERY_KEY = ['connectors'] as const;
export const CONNECTOR_TYPES_QUERY_KEY = ['connectorTypes'] as const;

function authHeaders(authToken: string) {
  return {
    'Content-Type': 'application/json',
    authorization: authToken,
  };
}

async function fetchConnectors(authToken: string): Promise<Connector[]> {
  const result = await request<ApiRespBody<Connector[]>>('/api/connectors', {
    method: 'GET',
    headers: authHeaders(authToken),
  });
  if (!result?.success || !result.data) {
    throw new Error(result?.message || 'Failed to load connectors');
  }
  return result.data;
}

async function fetchConnectorTypes(
  authToken: string
): Promise<ConnectorType[]> {
  const result = await request<ApiRespBody<ConnectorType[]>>(
    '/api/connectors/types',
    {
      method: 'GET',
      headers: authHeaders(authToken),
    }
  );
  if (!result?.success || !result.data) {
    throw new Error(result?.message || 'Failed to load connector types');
  }
  return result.data;
}

export function connectorsQueryOptions(authToken: string) {
  return queryOptions({
    queryKey: CONNECTORS_QUERY_KEY,
    queryFn: () => fetchConnectors(authToken),
  });
}

export function connectorTypesQueryOptions(authToken: string) {
  return queryOptions({
    queryKey: CONNECTOR_TYPES_QUERY_KEY,
    queryFn: () => fetchConnectorTypes(authToken),
  });
}

function useConnectorQueryError(error: unknown, caller: string) {
  useEffect(() => {
    if (!(error instanceof Error)) return;
    if (error.message === TOKEN_INVALID) {
      logout();
      return;
    }
    showSnackbar({
      autoHideDuration: null,
      caller,
      open: true,
      message: error.message,
      severity: 'error',
    });
  }, [caller, error]);
}

export function useConnectorsQuery() {
  const authToken = useAuthtoken();
  const query = useQuery(connectorsQueryOptions(authToken));
  useConnectorQueryError(query.error, 'connectors.query');
  return query;
}

export function useConnectorTypesQuery() {
  const authToken = useAuthtoken();
  const query = useQuery(connectorTypesQueryOptions(authToken));
  useConnectorQueryError(query.error, 'connectorTypes.query');
  return query;
}

export function useConnectorMutations() {
  const authToken = useAuthtoken();
  const queryClient = useQueryClient();
  const headers = authHeaders(authToken);

  function mutationOptions<TVars>(cfg: {
    label: string;
    caller: string;
    successMessage: string;
    run: (vars: TVars) => Promise<ApiRespBody | null>;
  }): UseMutationOptions<void, Error, TVars> {
    return {
      mutationFn: async (vars: TVars) => {
        setAppLoading({ isLoading: true, label: cfg.label });
        const result = await cfg.run(vars);
        if (!result?.success) {
          throw new Error(result?.message || `${cfg.caller} failed`);
        }
      },
      onSuccess: () => {
        showSnackbar({
            autoHideDuration: 5000,
            caller: cfg.caller,
            open: true,
            message: cfg.successMessage,
            severity: 'success',
          });
        queryClient.invalidateQueries({ queryKey: CONNECTORS_QUERY_KEY });
      },
      onError: (error) => {
        if (error.message === TOKEN_INVALID) {
          logout();
          return;
        }
        showSnackbar({
            autoHideDuration: null,
            caller: cfg.caller,
            open: true,
            message: error.message,
            severity: 'error',
          });
      },
      onSettled: () => {
        setAppLoading({ isLoading: false, label: '' });
      },
    };
  }

  const addMutation = useMutation(
    mutationOptions<Omit<Connector, 'connectorId'>>({
      label: 'Adding Connector',
      caller: 'onConnectorAdd',
      successMessage: 'Connector Added',
      run: (connector) =>
        request<ApiRespBody>('/api/connectors', {
          method: 'POST',
          body: JSON.stringify(connector),
          headers,
        }),
    })
  );

  const updateMutation = useMutation(
    mutationOptions<Connector>({
      label: 'Updating Connector',
      caller: 'onConnectorUpdate',
      successMessage: 'Connector Updated',
      run: (connector) =>
        request<ApiRespBody>('/api/connectors', {
          method: 'PUT',
          body: JSON.stringify(connector),
          headers,
        }),
    })
  );

  const deleteMutation = useMutation(
    mutationOptions<Connector>({
      label: 'Deleting Connector',
      caller: 'onConnectorDelete',
      successMessage: 'Connector Deleted',
      run: (connector) =>
        request<ApiRespBody>(`/api/connectors/${connector.connectorId}`, {
          method: 'DELETE',
          headers,
        }),
    })
  );

  return {
    addConnector: (connector: Omit<Connector, 'connectorId'>) =>
      addMutation.mutate(connector),
    updateConnector: (connector: Connector) => updateMutation.mutate(connector),
    deleteConnector: (connector: Connector) => deleteMutation.mutate(connector),
  };
}
